// ============================================================================
// EMPLOYEE MASTER SCREEN
// ============================================================================
// Master CRUD screen for prima.tbl_employee via prima-api /api/v1/Employees.
//
// Follows the ActivityMasterScreen pattern with additions:
//  - Multiple FK lookups: designations, offices, managers, partners, countries, states, timezones
//  - Cascading country -> state dropdown
//  - Complex formInitialData with workHours mapping

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { formatDateOnly } from 'utils';
import {
  useEmployees,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from 'hooks/api/useEmployee';
import { employeeService } from 'api/services/employeeService';
import { useDesignations } from 'hooks/api/useDesignation';
import { useOffices } from 'hooks/api/useOffice';
import { useCountries } from 'hooks/api/useCountry';
import { useStateProvinces } from 'hooks/api/useStateProvince';
import { useTimezones } from 'hooks/api/useTimezone';
import { useUserCache } from 'hooks/api/useUserCache';
import type {
  Employee,
  EmployeeSearchRequest,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
} from 'api/types';
import EmployeeFormModal, {
  type EmployeeFormData,
} from 'components/modals/modules/employee/EmployeeFormModal';
import EmployeeTerminateModal from 'components/modals/modules/employee/EmployeeTerminateModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
  { value: 'TERMINATED', label: 'Terminated only' },
];

const ROLE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'EMPLOYEE', label: 'Employee' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

// Column definitions for the ColumnSelector
const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',            label: 'Code',        field: 'code',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',            label: 'Employee',    field: 'name',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'userDisplayName', label: 'System User',  field: 'userDisplayName', required: false, defaultVisible: true,  sortable: true },
  { id: 'designationName', label: 'Designation', field: 'designationName', required: false, defaultVisible: true,  sortable: true },
  { id: 'officeName',      label: 'Location',    field: 'officeName',      required: false, defaultVisible: true,  sortable: true },
  { id: 'role',             label: 'Role',        field: 'isPartner',       required: false, defaultVisible: true,  sortable: false },
  { id: 'managerName',     label: 'Reporting Manager', field: 'managerName', required: false, defaultVisible: true,  sortable: true },
  { id: 'partnerName',     label: 'Reporting Partner', field: 'partnerName', required: false, defaultVisible: true,  sortable: true },
  { id: 'phone1',          label: 'Phone',       field: 'phone1',          required: false, defaultVisible: false, sortable: false },
  { id: 'status',          label: 'Status',      field: 'statusName',      required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt',       label: 'Created',     field: 'createdAt',       required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',       label: 'Updated',     field: 'updatedAt',       required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'employee-master-columns';

interface EmployeeMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const EmployeeMasterScreen: React.FC<EmployeeMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --------------------------------------------------------------------------
  // Column visibility state
  // --------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // --------------------------------------------------------------------------
  // Lookup data
  // --------------------------------------------------------------------------
  const { data: designations, refetch: refetchDesignations } = useDesignations({
    page: 1, pageSize: 100, includeInactive: false,
  });
  const { data: offices, refetch: refetchOffices } = useOffices({
    page: 1, pageSize: 100, includeInactive: false,
  });
  const { data: managersRaw, refetch: refetchManagers } = useEmployees({
    page: 1, pageSize: 100, isManager: true, includeInactive: false,
  });
  const { data: partnersRaw, refetch: refetchPartners } = useEmployees({
    page: 1, pageSize: 100, isPartner: true, includeInactive: false,
  });
  const { data: countries, refetch: refetchCountries } = useCountries();
  const { data: timezones, refetch: refetchTimezones } = useTimezones();
  const { data: users, refetch: refetchUsers } = useUserCache();

  // Cascading state for the form modal
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const { data: states } = useStateProvinces(selectedCountryId || null);

  const designationOptions = useMemo(
    () => designations.map((d) => ({ value: d.id, label: d.name })),
    [designations]
  );
  const officeOptions = useMemo(
    () => offices.map((o) => ({ value: o.id, label: o.name })),
    [offices]
  );
  const managerOptions = useMemo(
    () => managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner
        ? `★ ${e.code} - ${e.name} (Partner)`
        : `${e.code} - ${e.name}`,
    })),
    [managersRaw]
  );

  const userOptions = useMemo(
    () => users.map((u) => ({
      value: u.id,
      label: u.email ? `${u.displayName} (${u.email})` : u.displayName,
    })),
    [users]
  );
  const partnerOptions = useMemo(
    () => partnersRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` })),
    [partnersRaw]
  );

  // Map managerId → partnerId for auto-filling partner when manager is selected.
  // If the manager IS a partner themselves (isPartner=true), use their own ID
  // as the partner (self-reporting) — even if partner_id is NULL in the DB.
  const managerPartnerMap = useMemo(
    () => Object.fromEntries(
      managersRaw.map((e) => [
        e.id,
        e.partnerId || (e.isPartner ? e.id : ''),
      ]).filter(([, pid]) => pid !== '')
    ),
    [managersRaw]
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: `${c.countryCode} - ${c.countryName}` })),
    [countries]
  );
  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: `${s.provinceCode} - ${s.provinceName}` })),
    [states]
  );
  const timezoneOptions = useMemo(
    () => timezones.map((t) => ({ value: t.tzCode, label: `${t.utcOffsetDisplay || ''} ${t.tzName}`.trim() })),
    [timezones]
  );

  // Designation filter options
  const designationFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...designations.map((d) => ({ value: d.id, label: d.name }))],
    [designations]
  );
  const officeFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...offices.map((o) => ({ value: o.id, label: o.name }))],
    [offices]
  );
  const managerFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner ? `★ ${e.code} - ${e.name}` : `${e.code} - ${e.name}`,
    }))],
    [managersRaw]
  );
  const partnerFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...partnersRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` }))],
    [partnersRaw]
  );

  const loadLookups = useCallback(() => {
    refetchDesignations();
    refetchOffices();
    refetchManagers();
    refetchPartners();
    refetchCountries();
    refetchTimezones();
  }, [refetchDesignations, refetchOffices, refetchManagers, refetchPartners, refetchCountries, refetchTimezones]);

  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<EmployeeSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
    // status_id is the canonical lifecycle gate (ACTIVE / INACTIVE / TERMINATED).
    // Don't double-filter on date_left — that hid terminated rows from the master.
    includeLeft: true,
  });
  const { data, pagination, loading, refetch } = useEmployees(queryParams);

  // --------------------------------------------------------------------------
  // View state
  // --------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState<string>('');
  const [officeFilter, setOfficeFilter] = useState<string>('');
  const [managerFilter, setManagerFilter] = useState<string>('');
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Modal state
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Employee | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode || r.name === recordCode);
        if (rec) {
          setSelectedRecord(rec);
          if (rec.countryId) setSelectedCountryId(rec.countryId);
        }
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data, loadLookups]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Terminate state
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState<Employee | null>(null);

  // Mutation hooks
  const { createEmployee } = useCreateEmployee();
  const { updateEmployee } = useUpdateEmployee();
  const { deleteEmployee } = useDeleteEmployee();

  // --------------------------------------------------------------------------
  // Column definitions for SyutGrid
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Employee>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 120, enableSorting: true },
      {
        id: 'name',
        header: 'Employee',
        accessorKey: 'name',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            title={row.original.name}
            style={{
              maxWidth: '180px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.name}
          </div>
        ),
      },
      { id: 'userDisplayName', header: 'System User', accessorKey: 'userDisplayName', size: 150, enableSorting: true,
        cell: ({ row }) => row.original.userDisplayName || '-',
      },
      { id: 'designationName', header: 'Designation', accessorKey: 'designationName', size: 140, enableSorting: true,
        cell: ({ row }) => row.original.designationName || '-',
      },
      { id: 'officeName', header: 'Location', accessorKey: 'officeName', size: 120, enableSorting: true,
        cell: ({ row }) => row.original.officeName || '-',
      },
      {
        id: 'role',
        header: 'Role',
        accessorKey: 'isPartner',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => {
          const { isPartner, isManager } = row.original;
          if (isPartner) return <span className="badge badge-active">★ Partner</span>;
          if (isManager) return <span className="badge badge-pending">Manager</span>;
          return <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Employee</span>;
        },
      },
      { id: 'managerName', header: 'Reporting Manager', accessorKey: 'managerName', size: 160, enableSorting: true,
        cell: ({ row }) => row.original.managerName || '-',
      },
      { id: 'partnerName', header: 'Reporting Partner', accessorKey: 'partnerName', size: 160, enableSorting: true,
        cell: ({ row }) => row.original.partnerName || '-',
      },
      { id: 'phone1', header: 'Phone', accessorKey: 'phone1', size: 120, enableSorting: false,
        cell: ({ row }) => row.original.phone1 || '-',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const code = row.original.statusCode;
          const isActive = code === 'ACTIVE';
          const isTerminated = code === 'TERMINATED';
          // TERMINATED reuses the inactive badge style (red-ish) — the
          // statusName from the API already says "Terminated".
          const cls = isActive ? 'active' : 'inactive';
          return (
            <span
              className={`badge badge-${cls}`}
              title={isTerminated && row.original.terminationReason ? row.original.terminationReason : undefined}
            >
              {row.original.statusName || (isActive ? 'Active' : isTerminated ? 'Terminated' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Employee>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // --------------------------------------------------------------------------
  // Handlers -- open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setSelectedCountryId('');
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (record: Employee) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      // Fetch full record (with workHours) via GetById — list endpoint doesn't load child data
      try {
        const full = await employeeService.getById(record.id);
        setSelectedRecord(full.data ?? record);
      } catch {
        setSelectedRecord(record); // fallback to list data
      }
      setSelectedCountryId(record.countryId || '');
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: Employee) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  const handleTerminateClick = useCallback((record: Employee) => {
    setTerminateTarget(record);
    setTerminateModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Employee | null>(null);

  const handleView = useCallback(async (record: Employee) => {
    // Fetch full record (with workHours) for the view modal
    try {
      const full = await employeeService.getById(record.id);
      setViewRecord(full.data ?? record);
    } catch {
      setViewRecord(record);
    }
    setViewModalOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler
  // --------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: EmployeeFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        const parsedWorkHours = formData.workHours
          .filter((wh) => wh.minimumMinutes !== '' && wh.minimumMinutes !== '0')
          .map((wh) => ({
            dayOfWeek: wh.dayOfWeek,
            minimumMinutes: parseInt(wh.minimumMinutes, 10) || 0,
            description: wh.description || undefined,
          }));

        if (formMode === 'create') {
          const payload: CreateEmployeeRequest = {
            code: formData.code,
            name: formData.name,
            userId: formData.userId || undefined,
            dateOfJoining: formData.dateOfJoining,
            dateOfBirth: formData.dateOfBirth || undefined,
            designationId: formData.designationId,
            officeId: formData.officeId,
            isPartner: formData.isPartner || undefined,
            isManager: formData.isManager || undefined,
            managerId: formData.managerId || undefined,
            partnerId: formData.partnerId || undefined,
            address: formData.address || undefined,
            cityName: formData.cityName || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            postalCode: formData.postalCode || undefined,
            phone1: formData.phone1 || undefined,
            phone2: formData.phone2 || undefined,
            email: formData.email || undefined,
            standardRate: formData.standardRate ? parseFloat(formData.standardRate) : undefined,
            discountedRate: formData.discountedRate ? parseFloat(formData.discountedRate) : undefined,
            premiumRate: formData.premiumRate ? parseFloat(formData.premiumRate) : undefined,
            statusCode: formData.statusCode,
            workHours: parsedWorkHours.length > 0 ? parsedWorkHours : undefined,
          };
          await createEmployee(payload);
          notificationService.toast.success(`Employee "${payload.name}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateEmployeeRequest = {
            code: formData.code,
            name: formData.name,
            userId: formData.userId || undefined,
            dateOfJoining: formData.dateOfJoining,
            dateOfBirth: formData.dateOfBirth || undefined,
            designationId: formData.designationId,
            officeId: formData.officeId,
            isPartner: formData.isPartner || undefined,
            isManager: formData.isManager || undefined,
            managerId: formData.managerId || undefined,
            partnerId: formData.partnerId || undefined,
            address: formData.address || undefined,
            cityName: formData.cityName || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            postalCode: formData.postalCode || undefined,
            phone1: formData.phone1 || undefined,
            phone2: formData.phone2 || undefined,
            email: formData.email || undefined,
            standardRate: formData.standardRate ? parseFloat(formData.standardRate) : undefined,
            discountedRate: formData.discountedRate ? parseFloat(formData.discountedRate) : undefined,
            premiumRate: formData.premiumRate ? parseFloat(formData.premiumRate) : undefined,
            statusCode: formData.statusCode,
            dateLeft: formData.dateLeft || undefined,
            terminationReason: formData.terminationReason || undefined,
            rowVersion: selectedRecord.rowVersion,
            workHours: parsedWorkHours.length > 0 ? parsedWorkHours : undefined,
          };
          await updateEmployee(selectedRecord.id, payload);
          notificationService.toast.success(`Employee "${payload.name}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
        loadLookups();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save employee. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createEmployee, updateEmployee, refetch, loadLookups]
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteEmployee(deleteTarget.id);
      notificationService.toast.success(`Employee "${deleteTarget.name}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete employee.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteEmployee, refetch]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.designationId) count++;
    if (queryParams.officeId) count++;
    if (queryParams.managerId) count++;
    if (queryParams.partnerId) count++;
    if (queryParams.statusCode) count++;
    if (queryParams.isPartner != null || queryParams.isManager != null) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.designationId, queryParams.officeId, queryParams.managerId, queryParams.partnerId, queryParams.statusCode, queryParams.isPartner, queryParams.isManager]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      designationId: designationFilter || undefined,
      officeId: officeFilter || undefined,
      managerId: managerFilter || undefined,
      partnerId: partnerFilter || undefined,
      statusCode: statusFilter || undefined,
      isPartner: roleFilter === 'PARTNER' ? true : (roleFilter === 'EMPLOYEE' ? false : undefined),
      isManager: roleFilter === 'MANAGER' ? true : (roleFilter === 'EMPLOYEE' ? false : undefined),
      includeInactive: true,
      includeLeft: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, designationFilter, officeFilter, managerFilter, partnerFilter, statusFilter, roleFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setDesignationFilter('');
    setOfficeFilter('');
    setManagerFilter('');
    setPartnerFilter('');
    setStatusFilter('');
    setRoleFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      includeLeft: true,
      managerId: undefined,
      partnerId: undefined,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Employee): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [
        {
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        },
      ];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      // Terminate sits between Edit and Delete — partner-tier action, only
      // shown for employees who aren't already TERMINATED. The grant on
      // employee-master is `terminate` (see fix 014).
      if (allowedActions.includes('terminate') && row.statusCode !== 'TERMINATED') {
        items.push({
          id: 'terminate',
          label: 'Terminate',
          icon: <IconSystem.alert size={16} />,
          onClick: () => handleTerminateClick(row),
          variant: 'danger',
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleTerminateClick, handleDeleteClick]
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Employee',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  // Form initial data (edit mode)
  const formInitialData: EmployeeFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;

    const buildWorkHours = () => {
      if (selectedRecord.workHours && selectedRecord.workHours.length > 0) {
        const whMap = new Map(selectedRecord.workHours.map((wh) => [wh.dayOfWeek, wh]));
        return Array.from({ length: 7 }, (_, i) => {
          const day = i + 1;
          const existing = whMap.get(day);
          const totalMin = existing ? existing.minimumMinutes : 0;
          return {
            dayOfWeek: day,
            minimumMinutes: totalMin > 0 ? String(totalMin) : '',
            hours: totalMin > 0 ? String(Math.floor(totalMin / 60)) : '',
            minutes: totalMin > 0 ? String(totalMin % 60) : '',
            description: existing?.description || '',
          };
        });
      }
      return Array.from({ length: 7 }, (_, i) => ({
        dayOfWeek: i + 1,
        minimumMinutes: '',
        hours: '',
        minutes: '',
        description: '',
      }));
    };

    return {
      code: selectedRecord.code,
      name: selectedRecord.name,
      userId: selectedRecord.userId || '',
      dateOfJoining: selectedRecord.dateOfJoining ? selectedRecord.dateOfJoining.substring(0, 10) : '',
      dateOfBirth: selectedRecord.dateOfBirth ? selectedRecord.dateOfBirth.substring(0, 10) : '',
      designationId: selectedRecord.designationId || '',
      officeId: selectedRecord.officeId || '',
      isPartner: selectedRecord.isPartner,
      isManager: selectedRecord.isManager,
      managerId: selectedRecord.managerId || '',
      partnerId: selectedRecord.partnerId || '',
      address: selectedRecord.address || '',
      cityName: selectedRecord.cityName || '',
      countryId: selectedRecord.countryId || '',
      countryName: selectedRecord.countryName || '',
      stateId: selectedRecord.stateId || '',
      stateName: selectedRecord.stateName || '',
      postalCode: selectedRecord.postalCode || '',
      phone1: selectedRecord.phone1 || '',
      phone2: selectedRecord.phone2 || '',
      email: selectedRecord.email || '',
      standardRate: selectedRecord.standardRate != null ? String(selectedRecord.standardRate) : '',
      discountedRate: selectedRecord.discountedRate != null ? String(selectedRecord.discountedRate) : '',
      premiumRate: selectedRecord.premiumRate != null ? String(selectedRecord.premiumRate) : '',
      dateLeft: selectedRecord.dateLeft ? selectedRecord.dateLeft.substring(0, 10) : '',
      terminationReason: selectedRecord.terminationReason || '',
      statusCode: (selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
      workHours: buildWorkHours(),
    };
  }, [formMode, selectedRecord]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Employee Master"
        description="Manage employees, hierarchy, rates, and work hours"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.user size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, email...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* List view */}
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={
                  loading ? 'Loading employees...' : 'No employees found'
                }
              />
            )}

            {/* Grid (card) view */}
            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {loading ? 'Loading employees...' : 'No employees found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {item.code}
                            </div>
                            <h3
                              title={item.name}
                              style={{
                                fontSize: '1rem',
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                margin: 0,
                                marginBottom: '0.25rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.name}
                            </h3>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                              {item.designationName || '-'}
                            </div>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto', flexWrap: 'wrap' }}>
                          {item.officeName && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              {item.officeName}
                            </span>
                          )}
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Employee Name</label>
              <input
                type="text"
                className="form-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Designation"
                options={designationFilterOptions}
                value={designationFilter}
                onChange={(val) => setDesignationFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Location"
                options={officeFilterOptions}
                value={officeFilter}
                onChange={(val) => setOfficeFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Reporting Manager"
                options={managerFilterOptions}
                value={managerFilter}
                onChange={(val) => setManagerFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Reporting Partner"
                options={partnerFilterOptions}
                value={partnerFilter}
                onChange={(val) => setPartnerFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Role"
                options={ROLE_FILTER_OPTIONS}
                value={roleFilter}
                onChange={(val) => setRoleFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Form modal */}
      <EmployeeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        userOptions={userOptions}
        designationOptions={designationOptions}
        officeOptions={officeOptions}
        managerOptions={managerOptions}
        partnerOptions={partnerOptions}
        managerPartnerMap={managerPartnerMap}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        timezoneOptions={timezoneOptions}
        onCountryChange={(countryId) => setSelectedCountryId(countryId)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Employee"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}" (${deleteTarget.code})? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {/* Terminate modal */}
      <EmployeeTerminateModal
        isOpen={terminateModalOpen}
        employee={terminateTarget}
        onClose={() => { setTerminateModalOpen(false); setTerminateTarget(null); }}
        onTerminated={() => {
          setTerminateModalOpen(false);
          setTerminateTarget(null);
          refetch();
        }}
      />

      {/* View Modal — read-only employee details */}
      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-lg">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Employee — {viewRecord.code}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Name</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.name || '—'}</div>
                </div>
                <div>
                  <label className="form-label">System User</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.userDisplayName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Date of Joining</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDateOnly(viewRecord.dateOfJoining)}</div>
                </div>
                <div>
                  <label className="form-label">Designation</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.designationName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Location</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.officeName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.isPartner ? <span className="badge badge-active">★ Partner</span> : viewRecord.isManager ? <span className="badge badge-pending">Manager</span> : 'Employee'}</div>
                </div>
                <div>
                  <label className="form-label">Reporting Manager</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.managerName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Reporting Partner</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.partnerName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.phone1 || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.email || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.countryName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">State</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.stateName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">City</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.cityName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Standard Rate</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.standardRate != null ? viewRecord.standardRate.toFixed(2) : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Discounted Rate</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.discountedRate != null ? viewRecord.discountedRate.toFixed(2) : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Premium Rate</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.premiumRate != null ? viewRecord.premiumRate.toFixed(2) : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
                {viewRecord.dateLeft && (
                  <div>
                    <label className="form-label">Date Left</label>
                    <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDateOnly(viewRecord.dateLeft)}</div>
                  </div>
                )}
              </div>

              {/* Weekly Minimum Hours */}
              {viewRecord.workHours && viewRecord.workHours.length > 0 && (
                <>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
                    Weekly Minimum Hours
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => {
                      const wh = viewRecord.workHours?.find((w) => w.dayOfWeek === i + 1);
                      if (!wh || wh.minimumMinutes === 0) return null;
                      const hrs = Math.floor(wh.minimumMinutes / 60);
                      const mins = wh.minimumMinutes % 60;
                      return (
                        <div key={day} style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8125rem' }}>
                          <span style={{ width: '90px', fontWeight: 500 }}>{day}</span>
                          <span>{hrs > 0 ? `${hrs}h` : ''}{mins > 0 ? ` ${mins}m` : ''}{hrs === 0 && mins === 0 ? '-' : ''}</span>
                          {wh.description && <span style={{ color: 'var(--text-muted)' }}>— {wh.description}</span>}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeMasterScreen;
